import { useState } from 'react';
import { Edit, Trash2, Search, Check, X, Image as ImageIcon, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useLanguage } from '@/contexts/LanguageContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminProjectService } from '@/api/services/admin-project.service';
import { adminService } from '@/api/services/admin.service';
import { queryKeys } from '@/hooks/query-keys';
import type { ProjectFilters } from '@/api/types';
import { useToast } from '@/hooks/use-toast';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { getErrorMessage } from '@/api/errors/error-handler';

// Types
interface ProjectImage {
  id: number;
  image_path: string;
  url: string;
  order: number;
}

interface ClientData {
  id: number;
  company_name: string;
}

interface ProjectData {
  id: number;
  title: string;
  description?: string;
  location?: string;
  completed_date?: string;
  status: 'pending' | 'approved' | 'rejected';
  client?: ClientData | string;
  images?: ProjectImage[];
  created_at?: string;
  updated_at?: string;
}

interface ProjectsResponse {
  success: boolean;
  data: ProjectData[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

const getProjectSchema = (t: (key: string) => string) => z.object({
  title: z.object({
    en: z.string().min(1, t('common.englishRequired') || 'English is required'),
    ar: z.string().min(1, t('common.arabicRequired') || 'Arabic is required'),
  }),
  description: z.object({
    en: z.string().optional(),
    ar: z.string().optional(),
  }).optional(),
  location: z.object({
    en: z.string().optional(),
    ar: z.string().optional(),
  }).optional(),
  completed_date: z.string().optional(),
  status: z.enum(['pending', 'approved', 'rejected']).optional(),
});

type ProjectFormData = z.infer<ReturnType<typeof getProjectSchema>>;

const AdminClientProjects = () => {
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const projectSchema = getProjectSchema(t);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<number | ''>('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectData | null>(null);
  const [deletingProject, setDeletingProject] = useState<ProjectData | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<{ id: number; url: string }[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<number[]>([]);

  // Fetch clients for filter
  const { data: clientsData } = useQuery({
    queryKey: ['admin', 'clients', 'list'],
    queryFn: async () => {
      const response = await adminService.getClients({ limit: 100 } as any);
      return response;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Flatten clients from infinite query
  const clients = (clientsData?.data?.data || []) as Array<{ id: number; client_id?: number; company_name?: string; name?: string }>;

  // Get selected client name and client_id
  const selectedClient = clients.find(c => c.client_id === clientFilter || c.id === clientFilter);
  const selectedClientName = selectedClient?.company_name || selectedClient?.name || '';
  const selectedClientId = selectedClient?.client_id || selectedClient?.id;

  // Fetch projects
  const { data: projectsData, isLoading: loading } = useQuery({
    queryKey: queryKeys.adminProjects.list({ 
      status: statusFilter || undefined, 
      client_id: clientFilter || undefined 
    }),
    queryFn: async () => {
      const filters: ProjectFilters = {};
      if (statusFilter) filters.status = statusFilter as 'pending' | 'approved' | 'rejected';
      if (clientFilter) filters.client_id = clientFilter;
      const response = await adminProjectService.getProjects(filters);
      return response as ProjectsResponse;
    },
    staleTime: 30000,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: FormData }) =>
      adminProjectService.updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminProjects.all });
      toast({ title: t('portfolio.projectUpdated') || 'Project Updated' });
      setIsEditDialogOpen(false);
      setEditingProject(null);
      resetForm();
    },
    onError: (error) => {
      toast({ 
        title: t('portfolio.updateFailed') || 'Update Failed', 
        description: getErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminProjectService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminProjects.all });
      toast({ title: t('portfolio.projectDeleted') || 'Project Deleted' });
      setIsDeleteDialogOpen(false);
      setDeletingProject(null);
    },
    onError: (error) => {
      toast({ 
        title: t('portfolio.deleteFailed') || 'Delete Failed', 
        description: getErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });

  // Status mutation
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'pending' | 'approved' | 'rejected' }) =>
      adminProjectService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminProjects.all });
      toast({ title: t('portfolio.statusUpdated') || 'Status Updated' });
    },
    onError: (error) => {
      toast({ 
        title: t('portfolio.updateFailed') || 'Update Failed', 
        description: getErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });

  // Extract projects from response
  const projects = projectsData?.data || [];

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      title: { en: '', ar: '' },
      description: { en: '', ar: '' },
      location: { en: '', ar: '' },
      completed_date: '',
      status: 'pending',
    },
  });

  const resetForm = () => {
    form.reset({
      title: { en: '', ar: '' },
      description: { en: '', ar: '' },
      location: { en: '', ar: '' },
      completed_date: '',
      status: 'pending',
    });
    setSelectedFiles([]);
    setExistingImages([]);
    setImagesToDelete([]);
  };

  const handleEditProject = async (data: ProjectFormData) => {
    if (!editingProject) return;

    const formData = new FormData();
    formData.append('title[en]', data.title.en);
    formData.append('title[ar]', data.title.ar);
    if (data.description?.en) formData.append('description[en]', data.description.en);
    if (data.description?.ar) formData.append('description[ar]', data.description.ar);
    if (data.location?.en) formData.append('location[en]', data.location.en);
    if (data.location?.ar) formData.append('location[ar]', data.location.ar);
    if (data.completed_date) formData.append('completed_date', data.completed_date);
    if (data.status) formData.append('status', data.status);

    selectedFiles.forEach((file) => {
      formData.append('images[]', file);
    });

    imagesToDelete.forEach((id) => {
      formData.append('delete_images[]', id.toString());
    });

    updateMutation.mutate(
      { id: editingProject.id, data: formData },
      {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setEditingProject(null);
          resetForm();
        },
      }
    );
  };

  const handleDeleteProject = () => {
    if (!deletingProject) return;
    deleteMutation.mutate(deletingProject.id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setDeletingProject(null);
      },
    });
  };

  const handleStatusChange = (project: ProjectData, status: 'pending' | 'approved' | 'rejected') => {
    statusMutation.mutate({ id: project.id, status });
  };

  const openEditDialog = (project: ProjectData) => {
    setEditingProject(project);
    // Since title is a string from API, we need to handle it
    const titleStr = typeof project.title === 'string' ? project.title : '';
    form.reset({
      title: { en: titleStr, ar: titleStr },
      description: { en: project.description || '', ar: project.description || '' },
      location: { en: project.location || '', ar: project.location || '' },
      completed_date: project.completed_date || '',
      status: project.status,
    });
    setExistingImages(project.images?.map((img: ProjectImage) => ({ id: img.id, url: img.url })) || []);
    setImagesToDelete([]);
    setSelectedFiles([]);
    setIsEditDialogOpen(true);
  };

  const filteredProjects = projects.filter((project: ProjectData) => {
    const title = typeof project.title === 'string' ? project.title : '';
    return title.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getTitle = (project: ProjectData) => {
    return typeof project.title === 'string' ? project.title : '';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString.split('T')[0] || '-';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-green-500"><Check className="h-3 w-3 mr-1" />{t('common.approved') || 'Approved'}</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><X className="h-3 w-3 mr-1" />{t('common.rejected') || 'Rejected'}</Badge>;
      default:
        return <Badge variant="secondary">{t('common.pending') || 'Pending'}</Badge>;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg sm:text-xl">{t('admin.clientProjects') || 'Client Projects'}</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {t('admin.clientProjectsDesc') || 'Manage and approve client projects'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('common.search') || 'Search projects...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-xs sm:text-sm"
              />
            </div>
            
            {/* Client Filter */}
            <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={clientPopoverOpen}
                  className="w-full sm:w-[200px] justify-between text-xs sm:text-sm"
                >
                  {clientFilter ? selectedClientName || 'Select Client...' : 'All Clients'}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0">
                <Command>
                  <CommandInput 
                    placeholder="Search client..."
                    value={clientSearch}
                    onValueChange={setClientSearch}
                  />
                  <CommandList>
                    <CommandEmpty>No client found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setClientFilter('');
                          setClientPopoverOpen(false);
                        }}
                      >
                        <Check className={`mr-2 h-4 w-4 ${!clientFilter ? 'opacity-100' : 'opacity-0'}`} />
                        All Clients
                      </CommandItem>
                      {clients
                        .filter(c => {
                          const name = c.company_name || c.name || '';
                          return name.toLowerCase().includes(clientSearch.toLowerCase());
                        })
                        .map((client) => (
                          <CommandItem
                            key={client.id}
                            value={client.company_name || client.name || `client-${client.id}`}
                            onSelect={() => {
                              setClientFilter(client.client_id || client.id);
                              setClientPopoverOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${(clientFilter === client.client_id || clientFilter === client.id) ? 'opacity-100' : 'opacity-0'}`} />
                            {client.company_name || client.name}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-md px-3 py-2 text-xs sm:text-sm w-full sm:w-auto"
            >
              <option value="">{t('common.allStatuses') || 'All Statuses'}</option>
              <option value="pending">{t('common.pending') || 'Pending'}</option>
              <option value="approved">{t('common.approved') || 'Approved'}</option>
              <option value="rejected">{t('common.rejected') || 'Rejected'}</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-8 text-sm sm:text-base">{t('common.loading') || 'Loading...'}</div>
          ) : (
            <>
              {/* Mobile Cards View */}
              <div className="sm:hidden space-y-3">
                {filteredProjects.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    {t('admin.noProjects') || 'No projects found'}
                  </div>
                ) : (
                  filteredProjects.map((project: ProjectData) => (
                    <Card key={project.id} className="shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm truncate mb-2">{getTitle(project)}</h3>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('common.client') || 'Client'}:</span>
                                <span className="truncate">
                                  {project.client && typeof project.client === 'object' && 'company_name' in project.client 
                                    ? project.client.company_name 
                                    : (typeof project.client === 'string' ? project.client : '-')}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('common.status') || 'Status'}:</span>
                                {getStatusBadge(project.status || 'pending')}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('common.images') || 'Images'}:</span>
                                <span className="flex items-center gap-1">
                                  <ImageIcon className="h-3 w-3" />
                                  {project.images?.length || 0}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('common.completedDate') || 'Date'}:</span>
                                <span>{formatDate(project.completed_date)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            {project.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStatusChange(project, 'approved')}
                                  className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStatusChange(project, 'rejected')}
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(project)} className="h-8 w-8 p-0">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setDeletingProject(project); setIsDeleteDialogOpen(true); }}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">{t('common.title') || 'Title'}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('common.client') || 'Client'}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('common.status') || 'Status'}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('common.images') || 'Images'}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('common.completedDate') || 'Completed Date'}</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">{t('common.actions') || 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjects.map((project: ProjectData) => (
                      <TableRow key={project.id}>
                        <TableCell className="font-medium text-xs sm:text-sm">{getTitle(project)}</TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          {project.client && typeof project.client === 'object' && 'company_name' in project.client 
                            ? project.client.company_name 
                            : (typeof project.client === 'string' ? project.client : '-')}
                        </TableCell>
                        <TableCell>{getStatusBadge(project.status || 'pending')}</TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          <div className="flex items-center gap-1">
                            <ImageIcon className="h-4 w-4" />
                            {project.images?.length || 0}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">{formatDate(project.completed_date)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {project.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStatusChange(project, 'approved')}
                                  title={t('common.approve') || 'Approve'}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStatusChange(project, 'rejected')}
                                  title={t('common.reject') || 'Reject'}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(project)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setDeletingProject(project); setIsDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{t('portfolio.editProject') || 'Edit Project'}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">{t('portfolio.editProjectDesc') || 'Update project details'}</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleEditProject)} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="title.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.titleEn') || 'Title (English)'}</FormLabel>
                      <FormControl><Input {...field} className="text-xs sm:text-sm" /></FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="title.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.titleAr') || 'Title (Arabic)'}</FormLabel>
                      <FormControl><Input {...field} dir="rtl" className="text-xs sm:text-sm" /></FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="description.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.descriptionEn') || 'Description (English)'}</FormLabel>
                      <FormControl><Textarea {...field} className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.descriptionAr') || 'Description (Arabic)'}</FormLabel>
                      <FormControl><Textarea {...field} dir="rtl" className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="location.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.locationEn') || 'Location (English)'}</FormLabel>
                      <FormControl><Input {...field} className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="location.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.locationAr') || 'Location (Arabic)'}</FormLabel>
                      <FormControl><Input {...field} dir="rtl" className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="completed_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">{t('common.completedDate') || 'Completed Date'}</FormLabel>
                    <FormControl><Input type="date" {...field} className="text-xs sm:text-sm" /></FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">{t('common.status') || 'Status'}</FormLabel>
                    <FormControl>
                      <select {...field} className="w-full border rounded-md px-3 py-2 text-xs sm:text-sm">
                        <option value="pending">{t('common.pending') || 'Pending'}</option>
                        <option value="approved">{t('common.approved') || 'Approved'}</option>
                        <option value="rejected">{t('common.rejected') || 'Rejected'}</option>
                      </select>
                    </FormControl>
                  </FormItem>
                )}
              />

              {existingImages.length > 0 && (
                <div>
                  <FormLabel className="text-xs sm:text-sm">{t('portfolio.existingImages') || 'Existing Images'}</FormLabel>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                    {existingImages.map((img) => (
                      <div key={img.id} className="relative group">
                        <img src={img.url} alt="" className="w-full h-16 sm:h-20 object-cover rounded" />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                          onClick={() => {
                            setExistingImages(existingImages.filter((i) => i.id !== img.id));
                            setImagesToDelete([...imagesToDelete, img.id]);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <FormLabel className="text-xs sm:text-sm">{t('portfolio.addImages') || 'Add New Images'}</FormLabel>
                <Input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                  className="mt-2 text-xs sm:text-sm"
                />
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="w-full sm:w-auto">
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button type="submit" disabled={updateMutation.isPending} className="w-full sm:w-auto">
                  {updateMutation.isPending ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="w-[95vw]">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{t('common.deleteConfirm') || 'Delete Project'}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {t('portfolio.deleteWarning') || 'Are you sure you want to delete this project? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} className="w-full sm:w-auto">
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDeleteProject} disabled={deleteMutation.isPending} className="w-full sm:w-auto">
              {deleteMutation.isPending ? (t('common.deleting') || 'Deleting...') : (t('common.delete') || 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClientProjects;
